/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import type {RequiredAction} from '@fluxer/schema/src/domains/user/UserResponseSchemas';

export type VerificationMode = 'email' | 'phone' | 'email_or_phone';

export interface RequiredActionFlow {
	readonly actions: ReadonlyArray<RequiredAction>;
	readonly key: string;
	readonly mode: VerificationMode;
	readonly reverify: boolean;
	readonly requiresInboundPhone: boolean;
}

const EMAIL_OR_PHONE_ACTIONS: ReadonlyArray<RequiredAction> = [
	'REQUIRE_VERIFIED_EMAIL_OR_VERIFIED_PHONE',
	'REQUIRE_REVERIFIED_EMAIL_OR_VERIFIED_PHONE',
	'REQUIRE_VERIFIED_EMAIL_OR_REVERIFIED_PHONE',
	'REQUIRE_REVERIFIED_EMAIL_OR_REVERIFIED_PHONE',
];

const PHONE_ACTIONS: ReadonlyArray<RequiredAction> = [
	'REQUIRE_VERIFIED_PHONE',
	'REQUIRE_REVERIFIED_PHONE',
	'REQUIRE_INBOUND_PHONE_VERIFICATION',
];

const REVERIFY_ACTIONS: ReadonlyArray<RequiredAction> = [
	'REQUIRE_REVERIFIED_EMAIL',
	'REQUIRE_REVERIFIED_PHONE',
	'REQUIRE_REVERIFIED_EMAIL_OR_VERIFIED_PHONE',
	'REQUIRE_VERIFIED_EMAIL_OR_REVERIFIED_PHONE',
	'REQUIRE_REVERIFIED_EMAIL_OR_REVERIFIED_PHONE',
];

export function getRequiredActionsKey(requiredActions: ReadonlyArray<RequiredAction> | null | undefined): string | null {
	if (!requiredActions || requiredActions.length === 0) {
		return null;
	}
	return [...requiredActions].sort().join('|');
}

export function resolveRequiredActionFlow(
	requiredActions: ReadonlyArray<RequiredAction> | null | undefined,
): RequiredActionFlow | null {
	if (!requiredActions || requiredActions.length === 0) {
		return null;
	}

	const actions = [...requiredActions];
	const requiresInboundPhone = actions.includes('REQUIRE_INBOUND_PHONE_VERIFICATION');

	let mode: VerificationMode = 'email';
	if (actions.some((action) => EMAIL_OR_PHONE_ACTIONS.includes(action))) {
		mode = 'email_or_phone';
	} else if (requiresInboundPhone || actions.some((action) => PHONE_ACTIONS.includes(action))) {
		mode = 'phone';
	}

	return {
		actions,
		key: getRequiredActionsKey(actions)!,
		mode,
		reverify: actions.some((action) => REVERIFY_ACTIONS.includes(action)),
		requiresInboundPhone,
	};
}
